import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, Instagram, Facebook, Youtube, Clock } from 'lucide-react';
import { companyInfo } from '../mockData';
import { toast } from '../hooks/use-toast';
import { PageHero, Reveal, TechCard, IconBox } from '../components/ui-kit';

const fields = [
  { id: 'name', label: 'Nom complet', type: 'text', placeholder: 'Votre nom' },
  { id: 'email', label: 'Email', type: 'email', placeholder: 'votre.email@exemple.com' },
  { id: 'phone', label: 'Téléphone', type: 'tel', placeholder: '06 12 34 56 78' },
  { id: 'subject', label: 'Sujet', type: 'text', placeholder: 'Ex : Devis installation électrique' }
];

const hours = [
  ['Lundi – Vendredi', '8h00 – 18h00'],
  ['Samedi', '9h00 – 12h00'],
  ['Dimanche', 'Fermé']
];

const socials = [
  { icon: Instagram, href: companyInfo.socialMedia.instagram, label: 'Instagram', id: 'instagram' },
  { icon: Facebook, href: companyInfo.socialMedia.facebook, label: 'Facebook', id: 'facebook' },
  { icon: Youtube, href: companyInfo.socialMedia.youtube, label: 'YouTube', id: 'youtube' }
];

const inputCls = 'w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3.5 text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-energy focus:border-transparent transition-all';

const Contact = () => {
  const empty = { name: '', email: '', phone: '', subject: '', message: '' };
  const [form, setForm] = useState(empty);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    toast({ title: 'Message envoyé !', description: 'Nous vous recontacterons très prochainement.' });
    setForm(empty);
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        title={<>Parlons de <span className="text-energy-gradient">votre projet.</span></>}
        subtitle="Nous sommes à votre écoute pour réaliser vos projets. Réponse sous 24h, devis gratuit et sans engagement."
        image="/assets/projet18.jpg"
      />

      <section className="bg-[#F8FAFC] text-ink py-24 lg:py-32 circuit-grid-light" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <TechCard className="p-8 lg:p-10 always">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-energy">Formulaire</span>
              <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">Demande de devis</h2>
              <form onSubmit={onSubmit} className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                {fields.map((f) => (
                  <div key={f.id} className={f.id === 'subject' ? 'sm:col-span-2' : ''}>
                    <label htmlFor={f.id} className="block text-sm font-semibold mb-2">{f.label} *</label>
                    <input
                      id={f.id}
                      name={f.id}
                      type={f.type}
                      required
                      value={form[f.id]}
                      onChange={onChange}
                      placeholder={f.placeholder}
                      className={inputCls}
                      data-testid={`contact-form-${f.id}`}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-sm font-semibold mb-2">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    value={form.message}
                    onChange={onChange}
                    placeholder="Décrivez votre projet..."
                    className={inputCls}
                    data-testid="contact-form-message"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-form-submit-btn"
                  className="btn-energy sm:col-span-2 inline-flex items-center justify-center gap-2 font-bold text-white text-lg px-7 py-4 rounded-xl"
                >
                  <Send size={18} /> Envoyer la demande
                </button>
              </form>
            </TechCard>
          </Reveal>

          <div className="lg:col-span-5 space-y-5">
            {[
              { icon: MapPin, title: 'Adresse', tone: 'energy', content: <p className="text-slate-600">{companyInfo.address}</p>, id: 'address' },
              { icon: Phone, title: 'Téléphone', tone: 'signal', content: <><a href={`tel:${companyInfo.phone}`} data-testid="contact-phone-link" className="font-mono text-lg text-ink hover:text-energy transition-colors">06 99 13 45 11</a><p className="text-xs text-slate-500 mt-1">Dépannage d'urgence 7j/7</p></>, id: 'phone' },
              { icon: Mail, title: 'Email', tone: 'energy', content: <a href={`mailto:${companyInfo.email}`} data-testid="contact-email-link" className="text-ink hover:text-energy transition-colors">{companyInfo.email}</a>, id: 'email' }
            ].map((c, i) => (
              <Reveal key={c.id} delay={i * 90}>
                <TechCard className="group flex items-start gap-5" data-testid={`contact-info-${c.id}`}>
                  <IconBox icon={c.icon} tone={c.tone} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{c.title}</p>
                    <div className="mt-1.5">{c.content}</div>
                  </div>
                </TechCard>
              </Reveal>
            ))}

            <Reveal delay={300}>
              <div className="rounded-2xl bg-abyss text-white p-7 border border-white/10 brackets always signal">
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-signal" />
                  <h3 className="font-display text-lg font-bold">Horaires d'ouverture</h3>
                </div>
                <div className="mt-5 space-y-3">
                  {hours.map(([d, h]) => (
                    <div key={d} className="flex justify-between text-sm border-b border-white/10 pb-3 last:border-0 last:pb-0">
                      <span className="text-slate-300">{d}</span>
                      <span className="font-mono text-slate-100">{h}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex gap-3">
                  {socials.map(({ icon: Icon, href, label, id }) => (
                    <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`contact-social-${id}`} className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-slate-300 hover:text-white hover:border-energy hover:bg-energy/20 transition-all duration-300">
                      <Icon size={17} />
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative bg-abyss text-white" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber">Zone d'intervention</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">Angers (49) & périphérie</h2>
            </div>
            <p className="text-sm text-slate-400 max-w-sm">Maine-et-Loire et départements limitrophes, pour particuliers et professionnels.</p>
          </Reveal>
          <Reveal delay={100} className="rounded-3xl overflow-hidden border border-white/10 h-[420px] grayscale invert-[0.92] hue-rotate-180 contrast-[0.9]">
            <iframe
              title="TSE OUEST Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2710.8464562836645!2d-0.5560847!3d47.4739316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x480878c1e4c8e6e3%3A0x3f8d8c7e3c5c6e7d!2s30%20Boulevard%20du%20Doyenn%C3%A9%2C%2049100%20Angers!5e0!3m2!1sfr!2sfr!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
